//@ts-ignore
import { CLESSON_MODES } from '@yandex-int/k-common/constants';
import { LESSON_STATUS } from '@yandex-int/k-common/typings';
import React from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import * as classSelectors from 'common.components/classes/selectors';
import { ACCORDION_ITEMS } from 'common.components/lesson-view.assignment/typings';
import * as lessonEditorSelectors from 'common.components/lesson/selectors';
import { isLessonInProgress } from 'common.components/lesson/utils';
import withTime, { InjectedProps as WithTimeProps } from 'common.components/timer/withTime';
import * as userSelectors from 'common.components/user/selectors';
import { getIsStudentRegistrationLinkAvailableInTeam } from 'common.components/user/selectors';
import { ReduxUserStore } from 'common.components/user/typings';
import { getIsTeamFromMes } from 'store/external-lms/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import { PropsWithClassId } from 'utils/selectors/selectors';
import { AssignmentMainSettings } from '../__main-settings/assignment__main-settings';
import * as assignmentSelectors from '../selectors';

import * as selectors from './selectors';

interface Props extends WrappedComponentProps, WithTimeProps {
    activeSubscriptionSlug: string | null;
    teamId: number;
    onAccordionItemClick: (id: ACCORDION_ITEMS) => void;
    onAddStudentsClick: () => void;
    onSendStudentLinkClick: () => void;
    onToggleStudentAssignment: (selected: Array<number>) => void;
    onSelectMode: (mode: CLESSON_MODES) => void;
}

const AssignmentMainSettingsUnwrapped = (props: Props) => {
    const {
        activeSubscriptionSlug,
        intl,
        teamId,
        onAccordionItemClick,
        onAddStudentsClick,
        onSendStudentLinkClick,
        onToggleStudentAssignment,
        onSelectMode,
        now,
    } = props;

    const { search } = useLocation();
    const currentAccordionItem = useSelector(assignmentSelectors.getCurrentAccordionItem);
    const isEmptyTeam = useSelector((store) => {
        return classSelectors.getIsEmptyTeam(store, { classId: teamId });
    });
    const shouldHideLessonMode = useSelector(selectors.getShouldHideLessonMode);
    const shouldShowLmsLoader = useSelector((store) => {
        // Здесь неправильно определяются необходимые пропсы, поэтому меняем типы
        return assignmentSelectors.getShouldShowLmsLoader(store, { classId: teamId, search } as PropsWithClassId);
    });
    const lmsSlug = useSelector((store) => {
        return lessonEditorSelectors.getLessonCourseExternalLmsSlug(store, { classId: teamId });
    });
    const selectedStudents = useSelector(assignmentSelectors.getSelectedStudents);

    const isAllTeamCheckboxDisabled = useSelector((store) => selectors.getAllTeamCheckboxDisabled(store, { search }));
    const studentsError = useSelector(assignmentSelectors.getSelectedStudentsError);
    const lessonMode = useSelector(assignmentSelectors.getLessonMode) || CLESSON_MODES.LESSON;
    const shouldShowStudentsLoader = useSelector((store) => {
        return selectors.getShouldShowStudentsLoader(store, { classId: teamId });
    });
    const areDatesEditable = useSelector(lessonEditorSelectors.getCanEditAssignmentDates);
    const assignDateValue = useSelector((store) => {
        return assignmentSelectors.getAssignDateValue(store, { now });
    });
    const deadlineDateValue = useSelector((store) => {
        return assignmentSelectors.getDeadlineDateValue(store, { now });
    });
    const canEditTeam = useSelector((store: ReduxUserStore) => {
        return userSelectors.getCanEditClass(store, teamId);
    });
    const assignmentTable = useSelector(assignmentSelectors.getAssignmentTable);
    const lessonStatus = useSelector(lessonEditorSelectors.getLessonStatus) || LESSON_STATUS.NOT_ASSIGNED;
    const canAssignIndividually = useSelector(lessonEditorSelectors.getCanAssignIndividually);
    const canEditLessonMode = useSelector(lessonEditorSelectors.getCanEditLessonMode);
    const isLessonStatusInProgress = isLessonInProgress(lessonStatus);
    const schoolId = useSelector(userSelectors.getSchoolId);

    const inStudentLinkExperiment = useSelector((storeState) =>
        getIsStudentRegistrationLinkAvailableInTeam(storeState, { classId: teamId })
    );
    const isTeamFromMes = useSelector((storeState) => getIsTeamFromMes(storeState, { classId: teamId, search }));
    const savedOrFirstTeamId = useSelector(userSelectors.getSavedOrFirstTeamId);
    const isTeacherFromMes = getIsTeacherFromMes(search);

    return (
        <AssignmentMainSettings
            activeSubscriptionSlug={activeSubscriptionSlug}
            assignDateValue={assignDateValue}
            assignmentTable={assignmentTable}
            canAssignIndividually={canAssignIndividually}
            canChangeAssignmentDates={areDatesEditable && !isLessonStatusInProgress}
            canEditTeam={canEditTeam}
            currentAccordionItem={currentAccordionItem}
            deadlineDateValue={deadlineDateValue}
            inStudentLinkExperiment={inStudentLinkExperiment}
            intl={intl}
            isAllTeamCheckboxDisabled={isAllTeamCheckboxDisabled}
            isEmptyTeam={isEmptyTeam}
            isLessonTypeModeDisabled={!canEditLessonMode || isLessonStatusInProgress}
            isTeacherFromMes={isTeacherFromMes}
            isTeamFromMes={isTeamFromMes}
            lessonMode={lessonMode}
            lessonStatus={lessonStatus}
            lmsSlug={lmsSlug}
            onAccordionItemClick={onAccordionItemClick}
            onAddStudentsClick={onAddStudentsClick}
            onSelectMode={onSelectMode}
            onSendStudentLinkClick={onSendStudentLinkClick}
            onToggleStudentAssignment={onToggleStudentAssignment}
            savedOrFirstTeamId={savedOrFirstTeamId}
            schoolId={schoolId}
            selectedStudents={selectedStudents}
            shouldHideLessonMode={shouldHideLessonMode}
            shouldShowLmsLoader={shouldShowLmsLoader}
            shouldShowStudentsLoader={shouldShowStudentsLoader}
            studentsError={studentsError}
            teamId={teamId}
        />
    );
};

export const AssignmentMainSettingsContainer = injectIntl(withTime(AssignmentMainSettingsUnwrapped));
